import { Component, ChangeDetectionStrategy } from '@angular/core';
import { PagedObjectManagerComponent } from '../../../core/component/paged-object-manager/paged-object-manager';
import { ColumnVisibility } from '../../../core/model/column-definition';
import { FilterDefinitionItem } from '../../../core/model/filter-definition-item';
import { MenuItemDefinition } from '../../../core/model/menu-item-definition';
import { CommandDispatcher } from '../../../core/service/command/command-dispatcher.service';
import { ColumnDefinitionFactory } from '../../../core/service/factory/column-definition-factory.service';
import { MessageService } from '../../../core/service/message.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { LeaseAgreementStatus, RentalObjectCategory, SendMessageType } from '../../../rent/model/const/enums';
import { LeaseAgreement } from '../../../rent/model/lease-agreement';
import { LeaseAgreementService } from '../../../rent/service/data/lease-agreement.service';
import { mailCommand } from '../../model/const/command';
import { SendTenantMessageCommandHandlerResolver } from '../../service/command/send-tenant-message-command-handler-resolver';

@Component({
    selector: 'app-send-mail-to-tenant',
    templateUrl: './send-tenant-mails.component.html',
    styleUrls: ['./send-tenant-mails.component.scss'],
    providers: [SendTenantMessageCommandHandlerResolver],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SendTenantMailsComponent extends PagedObjectManagerComponent<LeaseAgreement> {
  constructor(
    private readonly _dataService: LeaseAgreementService,
    messageService: MessageService,
    columndDefinitionFactory: ColumnDefinitionFactory,
    navigationService: NavigationService,
    dispatcher: CommandDispatcher,
    handlerResolver: SendTenantMessageCommandHandlerResolver
  ) {
    super(_dataService, messageService, navigationService, dispatcher, handlerResolver);

    this.columnDefinition = [
      columndDefinitionFactory.create('SEND_TENANT_MAIL_LIST.LEASE_AGREEMENT_STATUS_COLUMN', 'statusText', { allowSort: this }),
      columndDefinitionFactory.create('SEND_TENANT_MAIL_LIST.LEASE_AGREEMENT_NUMBER_COLUMN', 'leaseAgreementNumber'),
      columndDefinitionFactory.create('SEND_TENANT_MAIL_LIST.RECIPIENT_ADDRESS_COLUMN', 'recipientAddress'),
      columndDefinitionFactory.create('LEASE_AGREEMENT.TENANT_ONE', 'tenantOneName', { serverSideSortBinding: 'tenantOne' }),
      columndDefinitionFactory.create('LEASE_AGREEMENT.TENANT_TWO', 'tenantTwoName', {
        serverSideSortBinding: 'tenantTwo',
        visibility: ColumnVisibility.optional,
      }),
      columndDefinitionFactory.create('RENTAL_OBJECT_FORM.PROPERTY_LABEL', 'propertyName', {
        serverSideSortBinding: 'propertyNumberAndName',
      }),
      columndDefinitionFactory.create('SEND_TENANT_MAIL_LIST.RENTAL_OBJECT_TYPE_COLUMN', 'rentalObjectType'),
    ];
    this.menuDefinition = [new MenuItemDefinition('LEASE_AGREEMENT.COMMAND.SEND_LETTER', mailCommand.sendTenantMail)];

    this.allowOpenOnRowClick = false;

    this.preferencesKey = 'message.send-tenant-mail';
  }

  menuDefinition: MenuItemDefinition[];

  ngOnInit() {
    this._dataService.sendMessageTypeRoute = 'letter-filter';
    this._dataService.sendMessageType = SendMessageType.Mail;

    this.AddSubscription(
      this._dataService.getFilterDefinition().subscribe(definition => {
        this.defaultFilters = definition.defaultFields;
        const companies = definition.companies.map(x => {
          return { caption: x.toString(), value: x.id };
        });
        companies.splice(0, 0, { caption: 'ALL_TEXT', value: '' });
        const properties = definition.properties.map(x => {
          return { caption: x.toString(), value: x.id };
        });
        properties.splice(0, 0, { caption: 'ALL_TEXT', value: '' });
        const buildings = definition.buildings.map(x => {
          return { caption: x.toString(), value: x.id };
        });
        buildings.splice(0, 0, { caption: 'ALL_TEXT', value: '' });
        const entrances = definition.entrances.map(x => {
          return { caption: x.toString(), value: x.id };
        });
        entrances.splice(0, 0, { caption: 'ALL_TEXT', value: '' });

        this.filterDefinition = [
          new FilterDefinitionItem(
            'COMMON.LEASE_AGREEMENT_STATUS',
            'status',
            definition.status,
            [
              { caption: 'ALL_TEXT', value: '0' },
              { caption: 'CONST.LEASE_AGREEMENT_STATUS_ACTIVE', value: '9' },
              { caption: 'CONST.LEASE_AGREEMENT_STATUS_DRAFT', value: LeaseAgreementStatus.Draft.toString() },
              { caption: 'CONST.LEASE_AGREEMENT_STATUS_COMING', value: LeaseAgreementStatus.Coming.toString() },
              { caption: 'CONST.LEASE_AGREEMENT_STATUS_VALID', value: LeaseAgreementStatus.Valid.toString() },
              { caption: 'CONST.LEASE_AGREEMENT_STATUS_TERMINATED', value: LeaseAgreementStatus.Terminated.toString() },
              { caption: 'CONST.LEASE_AGREEMENT_STATUS_EXPIRED', value: LeaseAgreementStatus.Expired.toString() },
            ],
            definition.selectedFields.includes('status')
          ),
          new FilterDefinitionItem(
            'COMMON.RENTAL_OBJECT_CATEGORY',
            'rentalObjectCategory',
            definition.rentalObjectCategory,
            [
              { caption: 'ALL_TEXT', value: null },
              { caption: 'CONST.RENTAL_OBJECT_CATEGORY_DWELLING', value: RentalObjectCategory.Dwelling },
              { caption: 'CONST.RENTAL_OBJECT_CATEGORY_PREMISES', value: RentalObjectCategory.Premises },
              { caption: 'CONST.RENTAL_OBJECT_CATEGORY_PARKING', value: RentalObjectCategory.Parking },
              { caption: 'CONST.RENTAL_OBJECT_CATEGORY_OTHER', value: RentalObjectCategory.Other },
            ],
            definition.selectedFields.includes('rentalObjectCategory')
          ),
          new FilterDefinitionItem(
            'COMPANY.LIST_HEADER',
            'company',
            definition.company,
            companies,
            definition.selectedFields.includes('company')
          ),
          new FilterDefinitionItem(
            'RENTAL_OBJECT_FORM.PROPERTY_LABEL',
            'property',
            definition.property,
            properties,
            definition.selectedFields.includes('property')
          ),
          new FilterDefinitionItem(
            'DWELLING_FORM.BUILDING_LABEL',
            'building',
            definition.building,
            buildings,
            definition.selectedFields.includes('building')
          ),
          new FilterDefinitionItem(
            'ENTRANCE.ENTRANCE',
            'entrance',
            definition.entrance,
            entrances,
            definition.selectedFields.includes('entrance')
          ),
          new FilterDefinitionItem(
            'COMMON.TENANT_NAME',
            'contactName',
            definition.contactName,
            null,
            definition.selectedFields.includes('contactName')
          ),
        ];
      })
    );
  }
}
